import { ArrowRight, ChevronRight, Star } from "lucide-react";
import PhoneMockup from "@/components/PhoneMockup";
import { AppStoreBadge } from "@/components/Brand";

const HERO_BG =
  "https://images.pexels.com/photos/13327298/pexels-photo-13327298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function HeroSection() {
  return (
    <section
      id="hero"
      className="relative min-h-[100svh] overflow-hidden bg-[#0a0a0a] flex items-center pt-28 pb-20"
      data-testid="section-hero"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/60 to-[#0a0a0a]" />
        <div className="absolute inset-0 nfp-grid-bg opacity-60" />
        <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[80%] h-72 rounded-full blur-[100px] bg-[#1FA2FF]/20" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 nfp-reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.25em] text-white/75 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] nfp-pulse" />
            Built by a Sports Scientist and Exercise Physiologist
          </div>

          <h1
            className="nfp-heading text-white text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.92] tracking-tighter"
            data-testid="hero-headline"
          >
            Train Smarter.<br />
            <span className="text-white">Perform </span>
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(90deg,#00E5FF,#1FA2FF)" }}
            >
              Better
            </span>
            <span className="text-white">.</span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
            data-testid="hero-subhead"
          >
            Structured fitness and sport-performance programming for athletes and
            everyday users who want real progress — without random workouts.
          </p>

          <p className="mt-3 text-sm text-white/50 max-w-xl">
            Built by a Sports Scientist and Exercise Physiologist. Refined by elite coaches and sports scientists.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <AppStoreBadge testId="hero-cta-appstore" />
            <a
              href="#programs"
              data-testid="hero-cta-programs"
              className="nfp-btn nfp-btn-secondary"
            >
              View Programs <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs text-white/55">
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#00E5FF] text-[#00E5FF]" />
              ))}
              <span className="ml-1">Built with elite coaches</span>
            </div>
            <span className="hidden sm:inline opacity-30">|</span>
            <span>Track &amp; Field · Boxing · Basketball</span>
            <span className="hidden sm:inline opacity-30">|</span>
            <span>Beginner → Professional</span>
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
          <PhoneMockup />
        </div>
      </div>

      {/* Scroll hint */}
      <a
        href="#problem"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40 hover:text-white text-[10px] tracking-[0.3em] uppercase flex items-center gap-2"
      >
        Scroll <ArrowRight className="w-3 h-3 rotate-90" />
      </a>
    </section>
  );
}
